import { routes } from "@/routes/utils";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  EMAIL_ID,
  ASSOCIATE_PROJECT_ID,
  FIRST_NAME,
  LAST_NAME,
  ADDRESS,
  CITY,
  COUNTRY,
  FORCE_PASSWORD,
  MOBILE_NO,
  STATE,
  TEMP_PASSWORD,
  ZIPCODE,
} from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  getRequiredFields,
  removeEmptyFields,
  roles,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { schema } from "./validations";
import { useEffect, useMemo, useState } from "react";
import {
  useAddEmployeeMutation,
  useAddUpdateTeamMutation,
  useGetCityListByState,
  useGetEmployeeById,
  useGetProjectList,
  useGetRoles,
  useGetStatesListByCountry,
  useUpdateEmployeeMutation,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components";
import { Eye, EyeOff } from "lucide-react";

const useAddEdit = () => {
  const { id, action } = useParams();
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState<string>("in");
  const [isPassword, setIsPassword] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const isView = action?.toLowerCase() == routes.view;
  const isEdit = action?.toLowerCase() == routes.edit;

  const form = useForm<any>({
    defaultValues: {
      [FIRST_NAME]: "",
      [LAST_NAME]: "",
      [MOBILE_NO]: "",
      [EMAIL_ID]: "",
      [TEMP_PASSWORD]: "",
      [FORCE_PASSWORD]: false,
      [ADDRESS]: "",
      [STATE]: "",
      [COUNTRY]: "1",
      [CITY]: "",
      [ZIPCODE]: "",
      [ASSOCIATE_PROJECT_ID]: [],
    },
    resolver: zodResolver(schema),
  });

  const [keys, setKeys] = useState<any>({
    project: Math.random() * 3,
    state: Math.random() * 3,
    city: Math.random() * 3,
  });

  const requiredFields = getRequiredFields(schema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.clientMgmt}`);
  };

  const title = useMemo(
    () => (id ? `${form.watch(FIRST_NAME)}'s Details` : "Add Client"),
    [form.watch(FIRST_NAME)]
  );

  const breadcrumbData = [
    {
      link: `/${routes.admin}/${routes.clientMgmt}`,
      name: `Client Management`,
    },
    {
      link: "",
      name: title,
    },
  ];

  const { data: employeeDetails } = useGetEmployeeById({
    id: id as string,
    queryParams: {
      enabled: !!id,
    },
  });

  const { data: projectList, isLoading: isProjectListLoading } =
    useGetProjectList();

  const { data: roleList, isLoading: isRoleLoading } =
    useGetRoles();

  const { data: stateList, isLoading: isLoadingStates } =
    useGetStatesListByCountry({
      countryId: "1",
    });

  const { data: cityList, isLoading: isLoadingCity } = useGetCityListByState({
    stateId: form.watch(STATE),
    queryParams: {
      enabled: !!form.watch(STATE),
    },
  });

  useEffect(() => {
    if (employeeDetails?.data) {
      form.reset({
        [FIRST_NAME]: employeeDetails?.data?.[FIRST_NAME]?.toString() || "",
        [LAST_NAME]: employeeDetails?.data?.[LAST_NAME]?.toString() || "",
        [MOBILE_NO]: employeeDetails?.data?.[MOBILE_NO]?.toString() || "",
        [EMAIL_ID]: employeeDetails?.data?.[EMAIL_ID]?.toString() || "",
        [TEMP_PASSWORD]: employeeDetails?.data?.[TEMP_PASSWORD]?.toString() || "",
        [FORCE_PASSWORD]: employeeDetails?.data?.[FORCE_PASSWORD] || false,
        [ADDRESS]: employeeDetails?.data?.[ADDRESS]?.toString() || "",
        [STATE]: employeeDetails?.data?.[STATE]?.toString() || "",
        [COUNTRY]: employeeDetails?.data?.[COUNTRY]?.toString() || "1",
        [CITY]: employeeDetails?.data?.[CITY]?.toString() || "",
        [ZIPCODE]: employeeDetails?.data?.[ZIPCODE]?.toString() || "",
        [ASSOCIATE_PROJECT_ID]: employeeDetails?.data?.associatedProjects?.map((project: any) => project?.projectId?.toString()) || [],
      });
      setKeys({
        project: Math.random() * 2,
      });
    }
  }, [employeeDetails]);

  const { mutateAsync: addUpdateInfo, isPending: isAddUpdatePending } =
  useAddUpdateTeamMutation({
    onError: (error?: any) => {
      toast({
        message:
        typeof error?.response?.data?.errors?.[0] === "string" ? error?.response?.data?.errors?.[0] :
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
  });
  
  const addProject = async (userId: string) => {
    try {
      const additionalPromises: Promise<any>[] = [];

      if (form.watch(ASSOCIATE_PROJECT_ID)) {
        form.watch(ASSOCIATE_PROJECT_ID)?.map((val: any) => {
          additionalPromises.push(
            addUpdateInfo({
              projectId: val as string,
              userId: userId,
              data: {
                roleId: roleList?.data?.find((data: any) => data?.name?.toUpperCase() == roles.customer.value)?.roleId,
              },
            })
          );
        });
  
        await Promise.all(additionalPromises);
      }
      toast({
        message: id ? "Client Updated Successfully" : "Client Added Successfully",
        type: "success",
      });

      onBackClick();
      queryClient.invalidateQueries({queryKey: ["users"]})
    } catch (error) {
      console.error("Error in post-success APIs:", error);
      toast({
        message: "Something went wrong while processing additional data.",
        type: "error",
      });
    }
  }

  const { mutate: addEmployee, isPending: isAddEmployeePending } =
    useAddEmployeeMutation({
      onError: (error?: any) => {
        toast({
          message:
          typeof error?.response?.data?.errors?.[0] === "string" ? error?.response?.data?.errors?.[0] :
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: (data: any) => {
        addProject(data?.data?.data?.userId);
      },
    });

  const { mutate: updateEmployee } = useUpdateEmployeeMutation({
    onError: (error?: any) => {
      toast({
        message:
        typeof error?.response?.data?.errors?.[0] === "string" ? error?.response?.data?.errors?.[0] :
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      addProject(id as string);
    },
  });

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (id) {
      updateEmployee({
        data: { ..._data, userType: roles.customer.value },
        id,
      });
    } else {
      delete _data[FORCE_PASSWORD];

      if (
        !_data[TEMP_PASSWORD] ||
        (_data[TEMP_PASSWORD] as string)?.length < 4
      ) {
        form.setError(TEMP_PASSWORD, {
          message: "Minimum length of password is 4.",
        });
        return;
      }

      addEmployee({
        data: { ..._data, userType: roles.customer.value },
      });
    }
  };

  const onPhoneCountryChange = (_: any, country: any) => {
    const code =
      country?.countryCode == "us"
        ? "USA"
        : country?.countryCode == "in"
          ? "IND"
          : country?.countryCode;
    if (code != countryCode) {
      setCountryCode(code);
      form.setValue(STATE, "");
      form.setValue(CITY, "");
      form.setValue(ZIPCODE, "");
    }
  };

  const onEyeIconClick = () => {
    setIsPassword(!isPassword);
  };

  const Icon = isPassword ? Eye : EyeOff;

  return {
    title,
    onBackClick,
    breadcrumbData,
    form,
    requiredFields,
    onSubmit,
    keys,
    onCancel: onBackClick,
    isAddEmployeePending,
    isView,
    projectList,
    isProjectListLoading,
    stateList,
    isLoadingStates,
    cityList,
    isLoadingCity,
    countryCode,
    onPhoneCountryChange,
    onEyeIconClick,
    Icon,
    isPassword,
    isEdit,
    id,
    isRoleLoading,
    isAddUpdatePending
  };
};

export default useAddEdit;
